import { getProfile, noteToFrequency } from '@swarasense/shared';
import type { PracticeLevel, RagaRecord } from '@swarasense/shared';
import type { PitchSample } from '../audio/engine.js';
import type { AudioEngine } from '../audio/engine.js';
import type { Store } from './store.js';
import { evaluateCalibration, median } from './calibration.js';
import { loadProfile, updateProfile } from '../api/profile.js';
import { hzToNearestNote, recommendSa } from '../api/personalization.js';
import {
  MIN_KNOWN_RAGAS,
  RAGA_PRESETS,
  loadKnownRagaNames,
  saveKnownRagaNames,
} from './ear-known-ragas.js';

/**
 * Connected-Swarasense (W3 + W5): the "Your voice" panel — the single home for
 * personalization, rendered inside the Sa popover (which every page can reach)
 * so it dissolves the old separate "You" tab. Top: the Sa **dropdowns**, always
 * usable on their own (pick your shruti and go) + "Detect from tanpura"; then an
 * optional **one-tap Find my Sa** range finder + your comfortable range; then
 * your **level**, **goals**, and **My ragas** (shared with ear training). The
 * practice summary lives on the home strip, not here.
 *
 * Renders the panel shell once, then updates each section in place (never
 * re-rendering the whole panel — the close button is wired by the caller).
 */

export interface YourVoiceDeps {
  store: Store;
  engine: AudioEngine;
  /** One mic listen resolving a held pitch (Hz) or null; `onHeard` streams the
   * live detected pitch for feedback. From `app.ts`'s `captureSustainedHz`. */
  captureSustainedHz: (onHeard?: (hz: number | null) => void) => Promise<number | null>;
  /** The transport's Sa label to keep in sync. */
  saLabel: HTMLElement;
  /** Every loaded raga record — the My-ragas search pool. */
  allRagas: () => RagaRecord[];
}

const PITCH_CLASSES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const OCTAVES = [2, 3, 4, 5];
const DETECT_DURATION_MS = 2500;

const LEVELS: { value: PracticeLevel; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];
const GOAL_PRESETS = [
  'Sing in tune',
  'Learn new ragas',
  'Practise every day',
  'Improve my gamakas',
  'Prepare for class',
] as const;

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
const noteLabel = (n: { pitchClass: string; octave: number }): string => `${n.pitchClass}${n.octave}`;
const opt = (v: string, label: string, sel: boolean): string =>
  `<option value="${v}"${sel ? ' selected' : ''}>${label}</option>`;

export function renderYourVoice(panel: HTMLElement, deps: YourVoiceDeps, signal: AbortSignal): void {
  const { store } = deps;

  // Range-finder sub-state (kept across voice-section re-renders).
  let finder: {
    step: 'idle' | 'low' | 'high' | 'result' | 'notheard';
    lowHz?: number;
    highHz?: number;
    reco?: { pitchClass: string; octave: number; tightRange: boolean };
  } = { step: 'idle' };

  panel.innerHTML = `
    <div class="popover-head">
      <h2 id="sa-popover-title">Your voice</h2>
      <button class="popover-close" id="sa-close" type="button" aria-label="Close">&times;</button>
    </div>
    <div class="yv-section">
      <div class="field">
        <label for="sa-pitch-sel">Sa — pitch class</label>
        <select id="sa-pitch-sel" aria-label="Sa pitch class"></select>
      </div>
      <div class="field">
        <label for="sa-oct-sel">Octave</label>
        <select id="sa-oct-sel" aria-label="Sa octave"></select>
      </div>
      <button class="btn detect-btn" id="detect-drone" type="button">Detect from tanpura</button>
      <span id="cal-status" class="cal-status" aria-live="polite"></span>
    </div>
    <div class="yv-section" id="yv-voice"></div>
    <div class="yv-section" id="yv-level">
      <h3 class="yv-title">Your level</h3>
      <div class="seg yv-level-seg" role="group" aria-label="Level"></div>
    </div>
    <div class="yv-section" id="yv-goals">
      <h3 class="yv-title">Your goals</h3>
      <div class="you-chips" role="group" aria-label="Goals"></div>
    </div>
    <div class="yv-section" id="yv-ragas">
      <h3 class="yv-title">My ragas</h3>
      <p class="you-hint">Used to tailor ear training. Pick at least ${MIN_KNOWN_RAGAS}.</p>
      <input class="you-raga-search" id="yv-raga-search" type="search" placeholder="Search ragas to add…" aria-label="Search ragas to add" autocomplete="off" />
      <ul class="you-raga-results" id="yv-raga-results" hidden></ul>
      <div class="you-chips you-known" id="yv-known"></div>
      <div class="you-presets" id="yv-presets"></div>
    </div>`;

  const $ = <T extends HTMLElement>(id: string): T => panel.querySelector<T>(`#${id}`)!;
  const calStatusEl = $('cal-status');
  const pitchSel = $<HTMLSelectElement>('sa-pitch-sel');
  const octSel = $<HTMLSelectElement>('sa-oct-sel');

  // ---- Sa dropdowns (always usable) ----
  function refreshSaSelects(): void {
    const s = store.get();
    pitchSel.innerHTML = PITCH_CLASSES.map((p) => opt(p, p, p === s.pitchClass)).join('');
    octSel.innerHTML = OCTAVES.map((o) => opt(String(o), String(o), o === s.octave)).join('');
  }
  function updateSaLabel(): void {
    const cur = store.get();
    deps.saLabel.textContent = `${cur.pitchClass}${cur.octave}`;
  }
  function clearCalibrationStatus(): void {
    calStatusEl.textContent = '';
    calStatusEl.removeAttribute('data-state');
  }
  refreshSaSelects();
  updateSaLabel();
  if (store.get().calibration?.locked) {
    calStatusEl.textContent = `Locked: ${store.get().calibration!.saHz.toFixed(1)} Hz`;
    calStatusEl.setAttribute('data-state', 'ok');
  }

  pitchSel.addEventListener(
    'change',
    () => {
      store.set({ pitchClass: pitchSel.value, calibration: undefined });
      clearCalibrationStatus();
      updateSaLabel();
      renderVoice();
    },
    { signal },
  );
  octSel.addEventListener(
    'change',
    () => {
      store.set({ octave: Number(octSel.value), calibration: undefined });
      clearCalibrationStatus();
      updateSaLabel();
      renderVoice();
    },
    { signal },
  );

  const detectBtn = $<HTMLButtonElement>('detect-drone');
  detectBtn.addEventListener(
    'click',
    () => {
      void (async () => {
        if (deps.engine.running) return;
        detectBtn.disabled = true;
        calStatusEl.removeAttribute('data-state');
        calStatusEl.textContent = 'Listening… play your tanpura steadily.';
        const samples: PitchSample[] = [];
        try {
          const cur = store.get();
          await deps.engine.start(getProfile(cur.instrument), (sample) => samples.push(sample));
          await deps.engine.calibrateDrone(DETECT_DURATION_MS);
        } catch (e) {
          calStatusEl.textContent = `mic error: ${(e as Error).message}`;
          calStatusEl.setAttribute('data-state', 'error');
          detectBtn.disabled = false;
          return;
        } finally {
          await deps.engine.stop();
        }
        const voicedHz = samples.filter((p) => p.pitchHz !== null).map((p) => p.pitchHz!);
        const detectedHz = voicedHz.length > 0 ? median(voicedHz) : null;
        const cur = store.get();
        const expectedHz = noteToFrequency(`${cur.pitchClass}${cur.octave}`);
        const result = evaluateCalibration(
          samples.map((p) => p.clarity),
          detectedHz,
          expectedHz,
        );
        store.set({ calibration: { locked: result.ok, saHz: result.saHz } });
        calStatusEl.textContent = result.ok
          ? `Locked: ${result.saHz.toFixed(1)} Hz`
          : (result.reason ?? 'Could not get a clean read — try again.');
        calStatusEl.setAttribute('data-state', result.ok ? 'ok' : 'error');
        detectBtn.disabled = false;
      })();
    },
    { signal },
  );

  // ---- Find my Sa (one-tap range finder) ----
  const showHeard = (hz: number | null): void => {
    const el = panel.querySelector<HTMLElement>('#yv-heard');
    if (el) el.textContent = hz === null ? 'Listening…' : `Hearing ${noteLabel(hzToNearestNote(hz))} ♪`;
  };

  async function runFinder(): Promise<void> {
    finder = { step: 'low' };
    renderVoice();
    const low = await deps.captureSustainedHz(showHeard);
    if (low === null) {
      finder = { step: 'notheard' };
      renderVoice();
      return;
    }
    finder = { step: 'high', lowHz: low };
    renderVoice();
    const high = await deps.captureSustainedHz(showHeard);
    if (high === null) {
      finder = { step: 'notheard' };
      renderVoice();
      return;
    }
    const lo = Math.min(low, high);
    const hi = Math.max(low, high);
    finder = { step: 'result', lowHz: lo, highHz: hi, reco: recommendSa(lo, hi) };
    renderVoice();
  }

  function useRecommendedSa(): void {
    if (!finder.reco || finder.lowHz === undefined || finder.highHz === undefined) return;
    const { pitchClass, octave } = finder.reco;
    store.set({ pitchClass, octave, calibration: undefined });
    updateProfile({
      voiceRange: {
        lowHz: finder.lowHz,
        highHz: finder.highHz,
        lowNote: noteLabel(hzToNearestNote(finder.lowHz)),
        highNote: noteLabel(hzToNearestNote(finder.highHz)),
      },
    });
    refreshSaSelects();
    updateSaLabel();
    clearCalibrationStatus();
    finder = { step: 'idle' };
    renderVoice();
  }

  function renderVoice(): void {
    const card = $('yv-voice');
    const vr = loadProfile().voiceRange;
    let body = '';
    if (finder.step === 'idle') {
      body = `
        <h3 class="yv-title">Find your Sa</h3>
        ${
          vr
            ? `<p class="you-range">Comfortable range: <b>${esc(vr.lowNote)}</b> – <b>${esc(vr.highNote)}</b></p>`
            : `<p class="you-hint">Not sure of your shruti? Sing your lowest and highest notes and we'll suggest one.</p>`
        }
        <button class="btn primary" id="yv-find" type="button">${vr ? 'Find my Sa again' : 'Find my Sa'}</button>`;
    } else if (finder.step === 'low' || finder.step === 'high') {
      const which = finder.step === 'low' ? 'lowest' : 'highest';
      body = `
        <h3 class="yv-title">Find your Sa</h3>
        <p class="you-finder-step" aria-live="polite">Sing your <b>${which}</b> comfortable note, and hold it.</p>
        <p class="you-finder-heard" id="yv-heard" aria-live="off">Listening…</p>
        <button class="btn" id="yv-cancel" type="button">Cancel</button>`;
    } else if (finder.step === 'result' && finder.reco) {
      body = `
        <h3 class="yv-title">Find your Sa</h3>
        <p class="you-finder-result" aria-live="polite">We suggest <span class="you-sa-big">Sa = ${esc(noteLabel(finder.reco))}</span></p>
        ${finder.reco.tightRange ? `<p class="you-hint">Your range is a little under an octave — this Sa sits at the low end; it'll open up with practice.</p>` : ''}
        <div class="you-actions">
          <button class="btn primary" id="yv-use" type="button">Use this Sa</button>
          <button class="btn" id="yv-redo" type="button">Redo</button>
        </div>`;
    } else if (finder.step === 'notheard') {
      body = `
        <h3 class="yv-title">Find your Sa</h3>
        <p class="you-finder-step" aria-live="assertive">We couldn't hear a steady note. Check your mic and try again — or just pick your Sa above.</p>
        <div class="you-actions">
          <button class="btn primary" id="yv-redo" type="button">Try again</button>
          <button class="btn" id="yv-cancel" type="button">Not now</button>
        </div>`;
    }
    card.innerHTML = body;
    $('yv-find')?.addEventListener('click', () => void runFinder());
    $('yv-redo')?.addEventListener('click', () => void runFinder());
    $('yv-use')?.addEventListener('click', useRecommendedSa);
    $('yv-cancel')?.addEventListener('click', () => {
      finder = { step: 'idle' };
      renderVoice();
    });
    if (finder.step !== 'idle') card.querySelector<HTMLButtonElement>('.btn')?.focus();
  }
  renderVoice();

  // ---- Level (store) ----
  function renderLevel(): void {
    const level = store.get().level;
    $('yv-level').querySelector('.yv-level-seg')!.innerHTML = LEVELS.map(
      (l) =>
        `<button type="button" data-level="${l.value}"${l.value === level ? ' class="on" aria-pressed="true"' : ' aria-pressed="false"'}>${l.label}</button>`,
    ).join('');
    for (const btn of $('yv-level').querySelectorAll<HTMLButtonElement>('button')) {
      btn.addEventListener('click', () => {
        store.set({ level: btn.dataset.level as PracticeLevel });
        renderLevel();
      });
    }
  }
  renderLevel();

  // ---- Goals (profile) ----
  function renderGoals(): void {
    const goals = loadProfile().goals ?? [];
    $('yv-goals').querySelector('.you-chips')!.innerHTML = GOAL_PRESETS.map((g) => {
      const on = goals.includes(g);
      return `<button type="button" class="you-chip${on ? ' on' : ''}" data-goal="${esc(g)}" aria-pressed="${on ? 'true' : 'false'}">${esc(g)}</button>`;
    }).join('');
    for (const btn of $('yv-goals').querySelectorAll<HTMLButtonElement>('.you-chip')) {
      btn.addEventListener('click', () => {
        const goal = btn.dataset.goal!;
        const current = loadProfile().goals ?? [];
        updateProfile({
          goals: current.includes(goal) ? current.filter((g) => g !== goal) : [...current, goal],
        });
        renderGoals();
      });
    }
  }
  renderGoals();

  // ---- My ragas (shared with ear training) ----
  function renderRagas(): void {
    const known = loadKnownRagaNames();
    $('yv-known').innerHTML =
      known.length === 0
        ? '<span class="you-empty">None yet — search above or pick a starter set.</span>'
        : known
            .map(
              (name) =>
                `<button type="button" class="you-chip on" data-remove="${esc(name)}" aria-label="Remove ${esc(name)}">${esc(name)} <span aria-hidden="true">×</span></button>`,
            )
            .join('');
    $('yv-presets').innerHTML = RAGA_PRESETS.map(
      (p) => `<button type="button" class="btn you-preset" data-preset="${esc(p.id)}">${esc(p.label)}</button>`,
    ).join('');
    for (const btn of $('yv-known').querySelectorAll<HTMLButtonElement>('[data-remove]')) {
      btn.addEventListener('click', () => {
        saveKnownRagaNames(loadKnownRagaNames().filter((n) => n !== btn.dataset.remove));
        renderRagas();
      });
    }
    for (const btn of $('yv-presets').querySelectorAll<HTMLButtonElement>('.you-preset')) {
      btn.addEventListener('click', () => {
        const preset = RAGA_PRESETS.find((p) => p.id === btn.dataset.preset);
        if (preset) {
          saveKnownRagaNames(preset.ragaNames(deps.allRagas()));
          renderRagas();
        }
      });
    }
  }
  renderRagas();

  const search = $<HTMLInputElement>('yv-raga-search');
  const results = $('yv-raga-results');
  search.addEventListener(
    'input',
    () => {
      const q = search.value.trim().toLowerCase();
      if (!q) {
        results.hidden = true;
        results.innerHTML = '';
        return;
      }
      const known = new Set(loadKnownRagaNames());
      const matches = deps
        .allRagas()
        .filter((r) => r.name.toLowerCase().includes(q) && !known.has(r.name))
        .slice(0, 8);
      results.hidden = matches.length === 0;
      results.innerHTML = matches
        .map((r) => `<li><button type="button" class="you-raga-add" data-add="${esc(r.name)}">${esc(r.name)}</button></li>`)
        .join('');
      for (const btn of results.querySelectorAll<HTMLButtonElement>('.you-raga-add')) {
        btn.addEventListener('click', () => {
          saveKnownRagaNames([...loadKnownRagaNames(), btn.dataset.add!]);
          search.value = '';
          results.hidden = true;
          results.innerHTML = '';
          renderRagas();
        });
      }
    },
    { signal },
  );
}
